import { useState, useEffect, SyntheticEvent } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { UserClaims } from '@okta/okta-auth-js';
import { useStyles } from './style';
import { TaxTable } from './TaxTable';
import { StatementsTable } from './StatementsTable';
import { DocumentDto } from './models/ClientEntityDto';
import { ClientEntityDto } from './models/DocumentDto';
import { useAxios } from '../../hooks/useAxios';
import { DropdownSelect } from '@forgeglobal/forge-component-library';
import { DetailListItem } from '../../components/atoms/DetailListItem';
import { getConfig } from '../../providers/ConfigProvider';

/**
 * Tab panel component that conditionally renders content based on the active tab index.
 */
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div role="tabpanel" hidden={value !== index} data-test-id={`tabpanel-${index}`}>
    {value === index && <Box>{children}</Box>}
  </div>
);

export const PageContent = () => {
  const theme = useTheme();
  const { authState, oktaAuth } = useOktaAuth();
  const { classes } = useStyles();
  const [userClaims, setUserClaims] = useState<UserClaims>();
  const [selectedClientEntityCode, setSelectedClientEntityCode] =
    useState<string>();
  const [activeTab, setActiveTab] = useState(0);

  const isK1Enabled = getConfig().FEATURE_FLAGS_IS_K1_ENABLED === 'true';

  const { data: allDocuments } = useAxios<{ results: DocumentDto[] }>(
    '/documents/k1',
    'GET',
    undefined,
    isK1Enabled,
  );
  const { data: allStatements } = useAxios<{ results: DocumentDto[] }>(
    '/documents/statements',
    'GET',
  );
  const { data: clientEntityResults } = useAxios<{
    results: ClientEntityDto[];
  }>('/client-entities', 'GET');

  useEffect(() => {
    oktaAuth.getUser().then((userClaimsResult) => {
      setUserClaims(userClaimsResult);
    });
  }, [authState, oktaAuth]);

  useEffect(() => {
    if (
      clientEntityResults === null ||
      clientEntityResults.results.length === 0
    ) {
      return;
    }
    setSelectedClientEntityCode(clientEntityResults.results[0].Code);
  }, [clientEntityResults]);

  if (
    userClaims === undefined ||
    (isK1Enabled && allDocuments === null) ||
    allStatements === null ||
    clientEntityResults === null
  ) {
    return (
      <Box className={classes.spinnerContainer}>
        <CircularProgress />
      </Box>
    );
  }

  const documents = isK1Enabled
    ? (allDocuments?.results ?? []).filter(
        (d) => d.EntityCode === selectedClientEntityCode,
      )
    : [];

  const statements = allStatements.results ?? [];

  const selectedClientEntity = clientEntityResults.results.find(
    (c) => c.Code === selectedClientEntityCode,
  );

  const selectedClientEntityLabel =
    selectedClientEntity?.Name || selectedClientEntity?.Code;

  /**
   * Handles tab change between Tax Documents and Statements views.
   */
  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <>
      <div className={classes.item}>
        <div className={classes.inputWrapper}>
          <DropdownSelect
            data-test-id="client-dropdown-options"
            label="Client"
            value={selectedClientEntityCode}
            renderValue={(code: string) => selectedClientEntityLabel}
            size="medium"
            onChange={(e: any) => {
              setSelectedClientEntityCode(e.target.value);
            }}>
            {clientEntityResults.results.map((e) => (
              <MenuItem
                key={e.Code}
                value={e.Code}
                selected={e.Code === selectedClientEntityCode}>
                {e.Name || e.Code}
              </MenuItem>
            ))}
          </DropdownSelect>
        </div>
      </div>
      <div className={classes.item}>
        <Card
          sx={{
            backgroundColor: theme.palette.background.paper,
            boxShadow: 'none',
          }}>
          <CardContent>
            <Typography variant="h6">Client Information</Typography>
            <DetailListItem
              className={classes.cardItem}
              label={
                <Typography variant="body2" color="textSecondary">
                  Client Name
                </Typography>
              }
              content={
                <Typography data-test-id="client-name" variant="body1">
                  {selectedClientEntityLabel}
                </Typography>
              }
            />
            <DetailListItem
              className={classes.cardItem}
              label={
                <Typography variant="body2" color="textSecondary">
                  Email Address
                </Typography>
              }
              content={
                <Typography data-test-id="client-email" variant="body1">
                  {userClaims?.email}
                </Typography>
              }
            />
          </CardContent>
        </Card>
      </div>
      {isK1Enabled ? (
        <>
          <div className={classes.item}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              data-test-id="document-tabs"
            >
              <Tab label="Tax Documents" data-test-id="tab-tax-documents" />
              <Tab label="Statements" data-test-id="tab-statements" />
            </Tabs>
          </div>
          <TabPanel value={activeTab} index={0}>
            <TaxTable documents={documents} />
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <StatementsTable documents={statements} />
          </TabPanel>
        </>
      ) : (
        <div className={classes.item}>
          <StatementsTable documents={statements} />
        </div>
      )}
    </>
  );
};
